import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSessionStateStore, useSessionReset } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import { useCountdown } from '@renderer/utils/countdownUtils'
import { useInterval } from '@renderer/utils/useInterval'
import MidtransUtils from '@renderer/utils/midtransUtils'
import Spinner from '@renderer/components/Spinner'
import cogoToast from '@successtar/cogo-toast'
import { TransactionStatus } from '@renderer/types/sessionState'
import { formatAmount, normalizeCurrency } from '@renderer/utils/formatCurrency'

const Payment: React.FC = () => {
  const navigate = useNavigate()
  const { projectId } = useParams()
  const { config } = useConfigStore()
  const projectCurrency = normalizeCurrency(config?.resolvedCurrency)
  const { transaction, setSessionState, uploadSessionState, logActivity, id } =
    useSessionStateStore()
  const sessionReset = useSessionReset()
  const isMounted = useRef(true)

  // Helper to safely update state/logs only when component is mounted
  const safeUpdate = (callback: () => void): void => {
    if (isMounted.current) {
      callback()
    }
  }

  const [qrUrl, setQrUrl] = useState<string | null>(null)
  const [isQrLoaded, setIsQrLoaded] = useState(false)
  const [isExpired, setIsExpired] = useState(false)
  const [isCheckingPayment, setIsCheckingPayment] = useState(false)
  const [remainingTime, setRemainingTime] = useState<string>('--:--')

  const paymentConfig = config?.pages?.payment
  const paymentTimeout = paymentConfig?.settings?.paymentTimeout || 120

  // Initialize payment countdown
  const paymentCountdown = useCountdown(0)
  // Initialize auto-reset countdown (15 seconds)
  const autoResetCountdown = useCountdown(15)

  const calculateRemainingTime = (expiryTime: string, serverTime: string): number => {
    try {
      const expiryTimestamp = new Date(expiryTime).getTime()
      const serverTimestamp = new Date(serverTime).getTime()
      const remainingMs = expiryTimestamp - serverTimestamp
      return Math.max(0, Math.floor(remainingMs / 1000))
    } catch (error) {
      console.error('Error calculating remaining time:', error)
      return 0
    }
  }

  const handleCountdownEnd = async (): Promise<void> => {
    if (!isMounted.current) {
      return
    }
    if (id !== useSessionStateStore.getState().id) {
      logActivity('Ignoring timeout for a previous session.')
      return
    }
    await checkPaymentStatus()
    safeUpdate(() => {
      setIsCheckingPayment(false)
      setIsExpired(true)
      logActivity('Payment timed out.', {
        orderId: transaction.orderId
      })
      setSessionState({
        transaction: {
          status: 'expire'
        }
      })
    })
  }

  // Set up payment countdown callbacks
  useEffect(() => {
    paymentCountdown.setOnTick(() => {
      setRemainingTime(paymentCountdown.getFormattedRemainingSeconds())
    })
    paymentCountdown.setOnComplete(handleCountdownEnd)
  }, [])

  // Set up auto-reset countdown callbacks
  useEffect(() => {
    autoResetCountdown.setOnTick(() => {
      setRemainingTime(autoResetCountdown.getFormattedRemainingSeconds())
    })
    autoResetCountdown.setOnComplete(() => {
      sessionReset()
    })
  }, [])

  // Watch for payment expiration to start auto-reset countdown
  useEffect(() => {
    if (isExpired) {
      autoResetCountdown.start()
      return (): void => {
        autoResetCountdown.stop()
      }
    }
    return
  }, [isExpired])

  const cleanupPayment = useCallback(async (): Promise<void> => {
    if (transaction.orderId && transaction.status !== 'settlement') {
      try {
        await fetch(`${process.env.VITE_API_URL}/api/payments/${transaction.orderId}`, {
          method: 'DELETE',
          credentials: 'include'
        })
      } catch (error) {
        console.error('Error cancelling payment:', error)
      }
    }
  }, [transaction.orderId, transaction.status])

  const initiatePayment = async (): Promise<void> => {
    try {
      const data = await MidtransUtils.initiatePayment(
        transaction.amount || 0,
        id || '',
        paymentTimeout,
        projectId || '',
        '',
        '',
        transaction.discount || 0
      )

      if (data.order_id && data.qr_url) {
        const initialRemainingSeconds = calculateRemainingTime(data.expiry_time, data.server_time)

        if (initialRemainingSeconds <= 0) {
          setIsExpired(true)
          return
        }

        const sanitizedQrUrl = data.qr_url.replace(/([^:]\/)\/+/g, '$1')
        setQrUrl(sanitizedQrUrl)
        setIsQrLoaded(false)

        setSessionState({
          transaction: {
            orderId: data.order_id,
            amount: data.amount,
            qrUrl: sanitizedQrUrl,
            expiryTime: data.expiry_time,
            serverTime: data.server_time,
            status: 'pending' as TransactionStatus
          }
        })

        setIsExpired(false)
        paymentCountdown.stop()
        paymentCountdown.setRemainingSeconds(initialRemainingSeconds)
        paymentCountdown.start()
        setIsCheckingPayment(true)
        isMounted.current = true

        logActivity('Payment initiated.', {
          amount: transaction.amount,
          orderId: data.order_id,
          expiryTime: data.expiry_time,
          serverTime: data.server_time,
          remainingSeconds: initialRemainingSeconds
        })
      }
    } catch (e: unknown) {
      logActivity(`Payment initiation failed: ${(e as Error).message}`, {
        error: (e as Error).message,
        level: 'error'
      })
      console.error('Error initiating payment:', e)
      cogoToast.error((e as Error).message)
    }
  }

  const checkPaymentStatus = async (): Promise<void> => {
    if (!transaction.expiryTime || !transaction.serverTime || !isMounted.current) {
      return
    }

    try {
      const paymentData = await MidtransUtils.checkPaymentStatus(
        transaction.orderId || 'order-default-id'
      )

      if (paymentData.isExpired) {
        safeUpdate(() => {
          setIsCheckingPayment(false)
          setIsExpired(true)
          logActivity('Payment expired.', {
            orderId: transaction.orderId,
            expiryTime: transaction.expiryTime,
            serverTime: paymentData.serverTime,
            transactionStatus: paymentData.transactionStatus
          })
          setSessionState({
            transaction: {
              status: 'expire'
            }
          })
          uploadSessionState()
        })
        return
      }

      if (paymentData.paid) {
        safeUpdate(() => {
          setIsCheckingPayment(false)
          cogoToast.success('Payment successful!')
          logActivity('Payment successful.', {
            orderId: transaction.orderId,
            serverTime: paymentData.serverTime,
            transactionStatus: paymentData.transactionStatus
          })
          setSessionState({
            transaction: {
              status: 'settlement'
            }
          })
          uploadSessionState()
          navigate(`/${projectId}/capture`)
        })
      }
    } catch (e: unknown) {
      console.error('Error checking payment status:', e)
    }
  }

  useEffect(() => {
    initiatePayment()
    uploadSessionState()

    return (): void => {
      isMounted.current = false
      setIsCheckingPayment(false)
      paymentCountdown.stop()
      autoResetCountdown.stop()
      cleanupPayment()
    }
  }, [])

  useInterval(
    async () => {
      await checkPaymentStatus()
    },
    isCheckingPayment ? 5000 : null
  )

  const handleBack = (): void => {
    logActivity('User clicked back button on payment page')
    navigate(`/${projectId}/checkout`)
  }

  return (
    <div className="w-full h-full bg-white flex flex-col relative">
      {/* Back Button */}
      <div className="absolute bottom-12 left-24">
        <button
          onClick={handleBack}
          className={`font-body-yotta text-blueGrayYotta text-[32px] hover:opacity-80 transition-opacity active:scale-95 transform`}
        >
          ← Back
        </button>
      </div>

      {/* Title */}
      <div className="text-center pt-16 pb-12 max-w-3xl mx-auto">
        <h1 className="font-body-yotta text-blueGrayYotta text-[48px] font-semibold">
          Pindai QR untuk melakukan pembayaran
        </h1>
      </div>

      {/* QR Code Box */}
      <div className="flex items-center justify-center">
        <div className="bg-white border-4 border-gray-300 rounded-lg px-4 pb-8 shadow-2xl max-w-lg w-full">
          {/* QR Code */}
          <div className="flex justify-center mb-4">
            <div className="w-320 h-320 flex items-center justify-center">
              {qrUrl && (
                <img
                  src={qrUrl}
                  alt="QR Code"
                  className={`w-320 h-320 object-contain ${isQrLoaded ? 'block' : 'hidden'}`}
                  crossOrigin="anonymous"
                  onLoad={() => setIsQrLoaded(true)}
                />
              )}
              {(!qrUrl || !isQrLoaded) && (
                <div className="w-96 h-96 flex items-center justify-center">
                  <Spinner />
                </div>
              )}
            </div>
          </div>

          {/* Price */}
          <div className="text-center">
            <p className="text-redYotta text-[48px] font-bold">
              {formatAmount(transaction.amount || 0, projectCurrency)}
            </p>
          </div>
        </div>
      </div>

      {/* Timeout Circle */}
      <div className="absolute bottom-8 right-8">
        <div className="w-36 h-36 border-4 border-redYotta rounded-full flex items-center justify-center">
          <span className="text-redYotta text-4xl font-semibold">{remainingTime}</span>
        </div>
      </div>

      {/* Expired State */}
      {isExpired && (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-12 rounded-lg text-center shadow-2xl">
            <h2 className="text-3xl font-bold text-gray-700 mb-4">Payment Expired</h2>
            <p className="text-lg text-gray-600 mb-6">
              The payment code has expired. You will be redirected automatically.
            </p>
            <p className="text-sm text-gray-500">Auto-restart in {remainingTime}</p>
          </div>
        </div>
      )}
    </div>
  )
}

export default Payment
